import React, { FC, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { Dialog, TimezoneSelect } from '@modernloop/shared/components';
import { useFlag } from '@modernloop/shared/feature-flag';
import { Link, Stack, TextField, Typography } from '@mui/material';

import { AtsType } from 'src/generated/mloop-graphql';

import HelpCenterUrl from 'src/constants/HelpCenterUrl';

import { WorkdayIntegration, useWorkdayIntegrationPut } from 'src/hooks/api/integration';
import { getAtsService } from 'src/hooks/atsService';

export interface Props {
  onClose: () => void;
  workdayIntegration?: WorkdayIntegration;
  disabled?: boolean;
  onSave: () => void;
}

/**
 * Workday integration setup dialog.
 */
const WorkdayDialog: FC<Props> = ({ onClose, workdayIntegration, disabled = false, onSave }) => {
  const { t } = useTranslation();
  const [workday, setWorkday] = useState<WorkdayIntegration>(workdayIntegration || ({} as WorkdayIntegration));
  const putWorkday = useWorkdayIntegrationPut();

  const atsService = getAtsService(AtsType.Workday);

  const isWorkersDataEnabled = useFlag('org_workday_use_workers_data_report');

  return (
    <Dialog
      maxWidth="md"
      open
      onClose={onClose}
      title={t('hiring-dashboard-settings-integrations:settings.integrations.dialogTitle.workday')}
      subTitle={t('hiring-dashboard-settings-integrations:settings.integrations.workdayDialog.subTitle')}
      submitOptions={{
        isDisabled:
          disabled ||
          !workday.tenant ||
          !workday.web_service_url ||
          !workday.ui_url ||
          !workday.password ||
          !workday.username ||
          !workday.interview_raas_url ||
          !workday.job_application_raas_url,
        onClick: () => {
          putWorkday.mutate(
            {
              workday,
            },
            {
              onSuccess: onSave,
            }
          );
        },
        label: t('hiring-dashboard-common:common.save'),
      }}
      cancelOptions={{
        label: t('hiring-dashboard-common:common.cancel'),
        onClick: onClose,
      }}
      decoration={
        <img
          style={{ borderRadius: '6px' }}
          height="64px"
          width="64px"
          alt={t('hiring-dashboard-settings-integrations:settings.integrations.apiKeyDialog.logoAlt')}
          src={atsService.logoUrl}
        />
      }
    >
      <Stack spacing={2}>
        <Stack spacing={1}>
          <Stack>
            <Typography fontWeight={600}>
              {t('hiring-dashboard-settings-integrations:settings.integrations.workdayDialog.isuInfo')}
            </Typography>
            <Link underline="none" href={HelpCenterUrl.WORKDAY_INTEGRATION_ISU} target="_blank">
              {t('hiring-dashboard-settings-integrations:settings.integrations.workdayDialog.isuLearnMore')}
            </Link>
          </Stack>
          <TextField
            disabled={disabled}
            fullWidth
            id="outlined-basic"
            label={t('hiring-dashboard-settings-integrations:settings.integrations.workdayDialog.isuUsername')}
            variant="outlined"
            defaultValue={workday.username}
            onChange={(event) => setWorkday({ ...workday, username: event.target.value })}
          />
          <TextField
            disabled={disabled}
            fullWidth
            id="outlined-basic"
            label={t('hiring-dashboard-settings-integrations:settings.integrations.workdayDialog.password')}
            variant="outlined"
            defaultValue={workday.password}
            type="password"
            onChange={(event) => setWorkday({ ...workday, password: event.target.value })}
          />
        </Stack>

        <Stack spacing={1}>
          <Stack>
            <Typography fontWeight={600}>
              {t('hiring-dashboard-settings-integrations:settings.integrations.workdayDialog.primaryUrls')}
            </Typography>
            <Link underline="none" href={HelpCenterUrl.WORKDAY_INTEGRATION_WEB_SERVICES} target="_blank">
              {t('hiring-dashboard-settings-integrations:settings.integrations.workdayDialog.findUrls')}
            </Link>
          </Stack>
          <TextField
            disabled={disabled}
            fullWidth
            id="outlined-basic"
            inputProps={{ 'data-testid': 'workday-dialog-tenant-input-enabled-change' }}
            label={t('hiring-dashboard-settings-integrations:settings.integrations.workdayDialog.tenant')}
            variant="outlined"
            defaultValue={workday.tenant}
            onChange={(event) => setWorkday({ ...workday, tenant: event.target.value })}
          />
          <TextField
            disabled={disabled}
            fullWidth
            id="outlined-basic"
            label={t('hiring-dashboard-settings-integrations:settings.integrations.workdayDialog.webServicesUrl')}
            variant="outlined"
            defaultValue={workday.web_service_url}
            onChange={(event) => setWorkday({ ...workday, web_service_url: event.target.value })}
          />
          <TextField
            disabled={disabled}
            fullWidth
            id="outlined-basic"
            label={t('hiring-dashboard-settings-integrations:settings.integrations.workdayDialog.uiUrl')}
            variant="outlined"
            defaultValue={workday.ui_url}
            onChange={(event) => setWorkday({ ...workday, ui_url: event.target.value })}
          />
        </Stack>
        <Stack spacing={1}>
          <Stack>
            <Typography fontWeight={600}>
              {t('hiring-dashboard-settings-integrations:settings.integrations.workdayDialog.raasUrls')}
            </Typography>
            <Link underline="none" href={HelpCenterUrl.WORKDAY_INTEGRATION_RAAS_URLS} target="_blank">
              {t('hiring-dashboard-settings-integrations:settings.integrations.workdayDialog.raasLearnMore')}
            </Link>
          </Stack>
          <TextField
            disabled={disabled}
            fullWidth
            id="outlined-basic"
            label={t('hiring-dashboard-settings-integrations:settings.integrations.workdayDialog.interviews')}
            variant="outlined"
            defaultValue={workday.interview_raas_url}
            onChange={(event) => setWorkday({ ...workday, interview_raas_url: event.target.value })}
          />
          <TextField
            disabled={disabled}
            fullWidth
            id="outlined-basic"
            label={t('hiring-dashboard-settings-integrations:settings.integrations.workdayDialog.jobApplications')}
            variant="outlined"
            defaultValue={workday.job_application_raas_url}
            onChange={(event) => setWorkday({ ...workday, job_application_raas_url: event.target.value })}
          />
          {isWorkersDataEnabled && (
            <TextField
              disabled={disabled}
              fullWidth
              id="outlined-basic"
              label={t('hiring-dashboard-settings-integrations:settings.integrations.workdayDialog.workersData')}
              variant="outlined"
              defaultValue={workday.workers_data_raas_url}
              onChange={(event) => setWorkday({ ...workday, workers_data_raas_url: event.target.value })}
            />
          )}
        </Stack>

        <Stack spacing={1}>
          <Typography fontWeight={600}>
            {t('hiring-dashboard-settings-integrations:settings.integrations.workdayDialog.tenantTimezone')}
          </Typography>
          <Link underline="none" href={HelpCenterUrl.WORKDAY_INTEGRATION_TENANT_TIMEZONE} target="_blank">
            {t('hiring-dashboard-settings-integrations:settings.integrations.workdayDialog.timezoneLearnMore')}
          </Link>
          <TimezoneSelect
            value={workday.tenant_time_zone}
            onTimezoneChange={(timezone) => setWorkday({ ...workday, tenant_time_zone: timezone })}
          />
        </Stack>
      </Stack>
    </Dialog>
  );
};

export default WorkdayDialog;
